<template>
    <div class="d-flex h-100">
        <AutomationCategoriesNav
            @select="onAutomationCategorySelect"
            :type="type"
            :isEditMode="isEditMode"
            class="automation-categories-sidebar scrollable border-right p-3"/>
        <div class="cards-container scrollable flex-grow-1">
            <div v-if="isEditMode" class="cards-header border-bottom">
                <div class="text-right">
                    <button @click="addTemplate" class="btn btn-sm btn-primary add-template-btn">
                        Add
                    </button>
                </div>
            </div>
            <div class="cards">
                <div v-for="(templates, categoryUuid) in groupedTemplates" class="row mx-0">
                    <AutomationTemplateGroupHeader
                        :uuid="categoryUuid"
                        :type="type"
                        :id="`template-group-${categoryUuid}`"
                        class="col-12 px-0 pt-4"/>
                    <span v-if="templates.length === 0 && categoryUuid !== automationCategoryCustom.uuid"
                          class="text-muted pb-3 pt-2">
                        No templates yet
                    </span>
                    <template v-else>
                        <AutomationTemplateCard
                            v-for="(template, idx) in templates" :key="idx"
                            @click.native="onTemplateSelect(template)"
                            :template="template"
                            :isEditMode="isEditMode"
                            class="pl-0 pr-2 py-2"
                            :class="isEditMode ? 'col-6' : 'col-4'">
                            <template v-slot:card-footer>
                                <div v-if="isEditMode">
                                    <AutomationTemplateActions
                                        :template="template"
                                        @updated="item => $emit('update', item)"
                                        @deleted="item => $emit('delete', item)"/>
                                </div>
                                <template v-else>
                                    + Add to board
                                </template>
                            </template>
                        </AutomationTemplateCard>
                    </template>
                </div>
                <slot name="footer"></slot>
            </div>
        </div>
    </div>
</template>

<script>
import AutomationTemplateGroupHeader from './AutomationTemplateGroupHeader'
import AutomationCategoriesNav from './AutomationCategoriesNav'
import AutomationTemplateCard from './card/AutomationTemplateCard'
import AutomationTemplateActions from './card/AutomationTemplateActions'
import {AutomationTypes} from '@/types/automations/types'
import {automationCategoryCustom} from '@/types/automations/categories'
import {getCustomAutomationBaseModel} from '@/types/automations/utils'

export default {
    name: 'AutomationLibrary',
    inject: ['getAvailableCategories'],
    components: {
        AutomationTemplateActions,
        AutomationCategoriesNav,
        AutomationTemplateGroupHeader,
        AutomationTemplateCard
    },
    props: {
        type: {
            type: String,
            required: true,
            default: AutomationTypes.AUTOMATION
        },
        templates: {
            type: Array,
            required: true
        },
        isEditMode: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            automationCategoryCustom
        }
    },
    computed: {
        categories() {
            return this.getAvailableCategories({type: this.type, withCustom: !this.isMarketplace})
        },

        isMarketplace() {
            return this.type === AutomationTypes.MARKETPLACE
        },

        filteredCategories() {
            if (this.isEditMode) {
                return this.categories.filter(c => c.uuid !== automationCategoryCustom.uuid)
            }
            return this.categories
        },

        groupedTemplates() {
            return this.filteredCategories.reduce((obj, c) => ({
                ...obj,
                [c.uuid]: this.templates.filter(t => {
                    return _.isArray(t.categories) && t.categories.some(c2 => c2 === c.uuid)
                })
            }), {})
        }
    },
    methods: {
        scrollBoardToCategory(category) {
            const groupEl = document.getElementById(`template-group-${category}`)
            if (groupEl) {
                groupEl.scrollIntoView({behavior: 'smooth'})
            }
        },

        addTemplate() {
            this.$modal.show('automation-template-write', {
                operation: 'add',
                type: this.type
            })
        },

        onTemplateSelect(template) {
            this.$emit('select', template)
        },

        onAutomationCategorySelect(category) {
            if (category === automationCategoryCustom.uuid) {
                this.onTemplateSelect(getCustomAutomationBaseModel({type: this.type}))
            } else {
                this.scrollBoardToCategory(category)
            }
        }
    }
}
</script>

<style scoped lang="scss">
.cards-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    background-color: $color-white-muted;

    .cards-header {
        flex: 0 0 64px;
        padding: 16px;
        background: white;
    }

    .cards {
        flex: 0 0 1;
        overflow-y: auto;
        padding: 0 24px 24px 32px;
    }
}

.automation-categories-sidebar {
    flex: 0 0 240px;
    height: 100%;
}

.add-template-btn {
    margin-top: 0;
    width: 80px;
}
</style>
